/**
 * GoogleConsentMode — Google Consent Mode v2 integration.
 *
 * Pushes default and update signals to window.dataLayer.
 * Must be called BEFORE the GTM container loads for defaults to take effect.
 */

import type { ConsentDecisions } from "../core/StorageAdapter";

type GcmStatus = "granted" | "denied";

interface GcmState {
  ad_storage: GcmStatus;
  ad_user_data: GcmStatus;
  ad_personalization: GcmStatus;
  analytics_storage: GcmStatus;
  functionality_storage: GcmStatus;
  personalization_storage: GcmStatus;
  security_storage: GcmStatus;
  wait_for_update?: number;
}

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

export class GoogleConsentMode {
  private enabled: boolean;

  constructor(enabled: boolean) {
    this.enabled = enabled;
  }

  /**
   * Set GCM v2 defaults (denied for all non-essential). Call as early as
   * possible: from the embed tag, this runs before any other script.
   *
   * `first` is for defaults installed late, from the published banner config
   * rather than the embed tag, so a site that switches Consent Mode on never
   * has to re-copy its snippet. By then the page may already have queued gtag
   * commands. While no Google tag has loaded, dataLayer is still a plain array,
   * so the default is put at its front, where Google requires it to be. Once a
   * Google tag has taken over dataLayer.push, it can only be appended.
   */
  setDefaults(options: { first?: boolean } = {}): void {
    if (!this.enabled) return;
    this.ensureDataLayer();

    const state = {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: "denied",
      functionality_storage: "denied",
      personalization_storage: "denied",
      security_storage: "granted",
      wait_for_update: 500,
    } satisfies GcmState;

    if (options.first && window.dataLayer.push === Array.prototype.push) {
      window.dataLayer.unshift(gtagCommand("consent", "default", state));
      return;
    }
    window.gtag("consent", "default", state);
  }

  /** Update GCM v2 signals based on user's consent decisions */
  update(decisions: ConsentDecisions): void {
    if (!this.enabled) return;
    this.ensureDataLayer();

    const granted = (v: boolean): GcmStatus => (v ? "granted" : "denied");

    window.gtag("consent", "update", {
      ad_storage: granted(decisions.advertising),
      ad_user_data: granted(decisions.advertising),
      ad_personalization: granted(decisions.advertising),
      analytics_storage: granted(decisions.analytics),
      functionality_storage: granted(decisions.functional),
      personalization_storage: granted(decisions.functional),
      security_storage: "granted",
    } satisfies GcmState);

    // Push to dataLayer for GTM triggers
    window.dataLayer.push({
      event: "pb_consent_update",
      pb_analytics: decisions.analytics,
      pb_advertising: decisions.advertising,
      pb_functional: decisions.functional,
      pb_social: decisions.social,
    });
  }

  private ensureDataLayer(): void {
    window.dataLayer = window.dataLayer || [];
    if (!window.gtag) {
      // Google's own snippet: gtag pushes its `arguments` object. Google's tag
      // only treats Arguments objects as commands, so a stub that pushed a
      // plain array (as this one used to) queued consent commands Google
      // could ignore, on exactly the sites that load Beacon first.
      window.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments);
      };
    }
  }
}

/**
 * The gtag stub bootstraps published before 29 Sep 2026 install when the page
 * has none of its own: `function(...t){window.dataLayer.push(t)}` once
 * minified. It queues plain arrays, which Google's tag does not read as
 * commands (checked against gtag.js in Chromium: no consent state, no hits).
 */
const LEGACY_ARRAY_STUB = /^function\s*\(\s*\.\.\.\s*([\w$]+)\s*\)\s*\{\s*window\.dataLayer\.push\(\s*\1\s*\)\s*;?\s*\}$/;

const GTAG_COMMANDS = new Set(["consent", "js", "config", "event", "set", "get"]);

/**
 * Repair what an older bootstrap queued, from the core, which every site gets
 * through the signed release pointer with no snippet change.
 *
 * Runs while Google's tag has not loaded yet (the blocker holds it until
 * consent), so dataLayer is still a plain array and can be rewritten in place:
 * - a consent command queued as an array becomes the Arguments object Google
 *   reads, wherever it came from (a consent command is never anything else);
 * - if the page's gtag is still that older array stub, it is replaced with
 *   Google's form, and every gtag command it queued is converted too.
 * Once a Google tag has taken over dataLayer.push, nothing is touched.
 */
export function repairQueuedGtagCommands(): void {
  const layer = window.dataLayer;
  if (!Array.isArray(layer) || layer.push !== Array.prototype.push) return;

  const legacyStub =
    typeof window.gtag === "function" &&
    LEGACY_ARRAY_STUB.test(Function.prototype.toString.call(window.gtag).trim());
  if (legacyStub) {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }

  for (let i = 0; i < layer.length; i++) {
    const entry = layer[i];
    if (!Array.isArray(entry) || typeof entry[0] !== "string") continue;
    if (entry[0] === "consent" || (legacyStub && GTAG_COMMANDS.has(entry[0]))) {
      layer[i] = toArguments(entry);
    }
  }
}

function toArguments(values: unknown[]): IArguments {
  const collect = function (..._values: unknown[]): IArguments {
    // eslint-disable-next-line prefer-rest-params
    return arguments;
  };
  return collect(...values);
}

/** A queued gtag command in the form Google's tag reads: an Arguments object. */
function gtagCommand(command: string, action: string, params: object): IArguments {
  return (function (_command: string, _action: string, _params: object) {
    // eslint-disable-next-line prefer-rest-params
    return arguments;
  })(command, action, params);
}
